/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

* This template has been built upon, with additional code and configuration by Tazi Rides Ltd.
*/

import { useMemo, useState } from 'react'

import { Autocomplete, Icon, Switch, Tooltip } from '@mui/material'
import Grid from '@mui/material/Grid'
import { useQuery } from '@tanstack/react-query'
import axios from 'axios'

import MDBadgeDot from 'components/MDBadgeDot'
import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { useMaterialUIController } from 'context'
import ComplexStatisticsCard from 'modules/Cards/StatisticsCards/ComplexStatisticsCard'
import VerticalBarChart from 'modules/Charts/BarCharts/VerticalBarChart'
import DashboardLayout from 'modules/LayoutContainers/DashboardLayout'
import LoadingComponent from 'modules/Loading'
import DashboardNavbar from 'modules/Navbars/DashboardNavbar'
import { DashboardData } from 'types/server/dashboard'
import {
	formatCompactDifferential,
	formatCompactNumber,
	formatPercentage,
	formatPercentageDifferential,
} from 'utils/formatNumber'

type OutcomeCard = { current: number; differential: number }

type Denominator = { current: number; previous: number }

const totalCallsDenominator = (d: DashboardData): Denominator => {
	const current = d.cards.totalCalls.current
	return {
		current,
		previous: current - d.cards.totalCalls.differential,
	}
}

const excludedDenominator = (d: DashboardData): Denominator => {
	const total = totalCallsDenominator(d)
	const exclCurrent = d.cards.excludedTransfer.current
	const exclPrevious = exclCurrent - d.cards.excludedTransfer.differential
	return {
		current: total.current + exclCurrent,
		previous: total.previous + exclPrevious,
	}
}

const buildCallVolumeChart = (chart: DashboardData['lineChart'], asPercentage: boolean) => {
	// Stacks bottom-up, so reversing puts Unsuccessful at the baseline and Successful on top
	const outcomes = chart.datasets.filter(dataset => dataset.label !== 'Total Calls').reverse()
	const totals = chart.labels.map((_, i) =>
		outcomes.reduce((sum, dataset) => sum + dataset.data[i], 0)
	)
	return {
		totals,
		chart: {
			labels: chart.labels,
			datasets: asPercentage
				? outcomes.map(dataset => ({
						...dataset,
						data: dataset.data.map((value, i) => (totals[i] > 0 ? (value / totals[i]) * 100 : 0)),
				  }))
				: outcomes,
		},
	}
}

type TimeFrames =
	| 'Last 24 Hours'
	| 'Last 7 Days'
	| 'Last 14 Days'
	| 'This Month'
	| 'Last 30 Days'
	| 'Last 3 Months'
	| 'Last 6 Months'

type ViewModeIconProps = {
	icon: string
	label: string
	active: boolean
	onClick: () => void
}

function ViewModeIcon({ icon, label, active, onClick }: ViewModeIconProps): JSX.Element {
	return (
		<Tooltip title={label} placement="bottom" arrow>
			<MDBox
				component="button"
				type="button"
				aria-label={label}
				aria-pressed={active}
				onClick={onClick}
				display="flex"
				justifyContent="center"
				alignItems="center"
				width="2.5rem"
				height="2.5rem"
				borderRadius="lg"
				variant={active ? 'gradient' : 'contained'}
				bgColor={active ? 'primary' : 'grey-200'}
				color={active ? 'white' : 'secondary'}
				coloredShadow={active ? 'info' : 'none'}
				sx={{ border: 'none', cursor: 'pointer', transition: 'all 200ms ease' }}
			>
				<Icon fontSize="small">{icon}</Icon>
			</MDBox>
		</Tooltip>
	)
}

function InboundAnalytics(): JSX.Element {
	const [controller, _] = useMaterialUIController()
	const { session } = controller
	const [activeTimeFrame, setActiveTimeFrame] = useState<TimeFrames>('Last 24 Hours')
	const [showPercentages, setShowPercentages] = useState(true)

	const fetchData = async () => {
		const response = await axios.get<DashboardData>(
			`${process.env.REACT_APP_API_URL}/inbound/analytics`,
			{
				params: { timeframe: activeTimeFrame },
				headers: {
					Authorization: await session.user.getIdToken(),
				},
			}
		)
		return response.data
	}

	const { data, error, isLoading } = useQuery({
		queryKey: [`inbound-analytics-${activeTimeFrame}`],
		queryFn: fetchData,
	})

	const callVolume = useMemo(
		() => (data ? buildCallVolumeChart(data.lineChart, showPercentages) : null),
		[data, showPercentages]
	)

	const getOutcomeDisplay = (
		card?: OutcomeCard,
		getDenominator?: (d: DashboardData) => Denominator
	) => {
		if (!showPercentages || !data) {
			return {
				count: formatCompactNumber(card?.current),
				amount: formatCompactDifferential(card?.differential),
				differential: card?.differential ?? 0,
			}
		}
		const current = card?.current ?? 0
		const previous = current - (card?.differential ?? 0)
		const { current: denomCurrent, previous: denomPrevious } =
			getDenominator?.(data) ?? totalCallsDenominator(data)
		const currentPct = denomCurrent > 0 ? (current / denomCurrent) * 100 : 0
		const previousPct = denomPrevious > 0 ? (previous / denomPrevious) * 100 : 0
		const differential = Number((currentPct - previousPct).toFixed(1))
		return {
			count: formatPercentage(currentPct),
			amount: formatPercentageDifferential(differential),
			differential,
		}
	}

	const getOperatorOrAutomationDisplay = () => {
		if (!data) {
			return { count: 'N/A', amount: 'N/A', differential: 0 }
		}
		const totalCurrent = data.cards.totalCalls.current
		const opCurrent = data.cards.operatorTransfer.current
		const automatedCurrent = totalCurrent - opCurrent

		const totalPrevious = totalCurrent - data.cards.totalCalls.differential
		const opPrevious = opCurrent - data.cards.operatorTransfer.differential
		const automatedPrevious = totalPrevious - opPrevious

		if (!showPercentages) {
			const differential = opCurrent - opPrevious
			return {
				count: formatCompactNumber(opCurrent),
				amount: formatCompactDifferential(differential),
				differential,
			}
		}

		const currentRate = totalCurrent > 0 ? (automatedCurrent / totalCurrent) * 100 : 0
		const previousRate = totalPrevious > 0 ? (automatedPrevious / totalPrevious) * 100 : 0
		const differential = Number((currentRate - previousRate).toFixed(1))
		return {
			count: formatPercentage(currentRate),
			amount: formatPercentageDifferential(differential),
			differential,
		}
	}

	const cardDisplays = data
		? {
				successful: getOutcomeDisplay(data.cards.successful),
				ambiguous: getOutcomeDisplay(data.cards.ambiguous),
				unsuccessful: getOutcomeDisplay(data.cards.unsuccessful),
				newRides: getOutcomeDisplay(data.cards.newRidesByIntent),
				ridesUpdated: getOutcomeDisplay(data.cards.ridesUpdated),
				ridesCancelled: getOutcomeDisplay(data.cards.ridesCancelled),
				bookingConfirmations: getOutcomeDisplay(data.cards.bookingConfirmations),
				excluded: getOutcomeDisplay(data.cards.excludedTransfer, excludedDenominator),
				automation: getOperatorOrAutomationDisplay(),
			}
		: null

	const toPercentageLineChart = (chart: DashboardData['lineChart']): DashboardData['lineChart'] => {
		const totalDataset =
			chart.datasets.find(dataset => /total/i.test(dataset.label)) ??
			chart.datasets.find(dataset => dataset.color === 'info')
		if (!totalDataset) {
			return chart
		}
		return {
			...chart,
			datasets: chart.datasets.map(dataset =>
				dataset === totalDataset
					? { ...dataset, data: dataset.data.map(() => 100) }
					: {
							...dataset,
							data: dataset.data.map((value, i) => {
								const total = totalDataset.data[i]
								return total > 0 ? Number(((value / total) * 100).toFixed(1)) : 0
							}),
					  }
			),
		}
	}

	const lineChart = useMemo(
		() =>
			data ? (showPercentages ? toPercentageLineChart(data.lineChart) : data.lineChart) : undefined,
		[data, showPercentages]
	)

	const allTimeFrames: TimeFrames[] = [
		'Last 24 Hours',
		'Last 7 Days',
		'Last 14 Days',
		'Last 30 Days',
		'Last 3 Months',
		'Last 6 Months',
	]

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<MDBox mt={1.5}>
				<MDBox display="flex" justifyContent="space-between" alignItems="center" mb={4} mx={4}>
					<MDBox display="flex" alignItems="center">
						<ViewModeIcon
							icon="percent"
							label="Show percentages"
							active={showPercentages}
							onClick={() => setShowPercentages(true)}
						/>
						<Switch
							checked={!showPercentages}
							onChange={e => setShowPercentages(!e.target.checked)}
							inputProps={{ 'aria-label': 'Toggle between percentages and counts' }}
							sx={{ mx: 1 }}
						/>
						<ViewModeIcon
							icon="tag"
							label="Show counts"
							active={!showPercentages}
							onClick={() => setShowPercentages(false)}
						/>
					</MDBox>
					<MDBox display="flex" alignItems="center">
						<MDTypography variant="h6" color="secondary">
							Time Frame:&nbsp;&nbsp;&nbsp;
						</MDTypography>
						<Autocomplete
							disableClearable
							value={activeTimeFrame}
							options={allTimeFrames}
							onChange={(event, newValue) => {
								setActiveTimeFrame(newValue)
							}}
							size="medium"
							sx={{ width: '10rem' }}
							renderInput={params => <MDInput {...params} />}
						/>
					</MDBox>
				</MDBox>
				<Grid container spacing={3}>
					<Grid item xs={12} md={6} lg={3}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									icon="leaderboard"
									title="Total"
									color="info"
									tooltip="Excludes cals from excluded numbers"
									count={formatCompactNumber(data.cards.totalCalls.current)}
									percentage={{
										color: `${data.cards.totalCalls.differential < 0 ? 'error' : 'success'}`,
										amount: formatCompactDifferential(data.cards.totalCalls.differential),
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>
					<Grid item xs={12} md={6} lg={3}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="success"
									icon="check_circle"
									title="Successful"
									tooltip="The request was completed or appropriately transferred for non-automated matters."
									count={cardDisplays!.successful.count}
									percentage={{
										color: `${
											cardDisplays!.successful.differential < 0 ? 'error' : 'success'
										}`,
										amount: cardDisplays!.successful.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>
					<Grid item xs={12} md={6} lg={3}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="warning"
									icon="help"
									tooltip="The call was ended by the caller prior to completion or transfer."
									title="Ambiguous"
									count={cardDisplays!.ambiguous.count}
									percentage={{
										color: `${
											cardDisplays!.ambiguous.differential > 0 ? 'error' : 'success'
										}`,
										amount: cardDisplays!.ambiguous.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>
					<Grid item xs={12} md={6} lg={3}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="error"
									icon="cancel"
									title="Unsuccessful"
									tooltip="The AI couldn’t complete an automatable task and transferred the call for support."
									count={cardDisplays!.unsuccessful.count}
									percentage={{
										color: `${
											cardDisplays!.unsuccessful.differential > 0 ? 'error' : 'success'
										}`,
										amount: cardDisplays!.unsuccessful.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
			<Grid container spacing={3}>
				<Grid item xs={12}>
					{!isLoading && !error ? (
						<VerticalBarChart
							title="Call Volume"
							height={'25rem'}
							description={
								<MDBox display="flex" justifyContent="space-between">
									<MDBox display="flex" ml={-1}>
										<MDBadgeDot color="success" size="sm" badgeContent="Successful Calls" />
										<MDBadgeDot color="warning" size="sm" badgeContent="Ambiguous Calls" />
										<MDBadgeDot color="error" size="sm" badgeContent="Unsuccessful Calls" />
									</MDBox>
									<MDBox mt={-4} mr={-1} position="absolute" right="1.5rem">
										<Tooltip title="See the call volume overtime" placement="left" arrow>
											<MDButton variant="outlined" color="secondary" size="small" circular iconOnly>
												<Icon>priority_high</Icon>
											</MDButton>
										</Tooltip>
									</MDBox>
								</MDBox>
							}
							chart={callVolume!.chart}
							totals={callVolume!.totals}
							percentage={showPercentages}
							stacked
						/>
					) : (
						<LoadingComponent type="box" />
					)}
				</Grid>
			</Grid>
			<MDBox mt={3}>
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="success"
									icon="local_taxi"
									title="New Rides"
									tooltip="Calls where the caller intended to book a new ride."
									count={cardDisplays!.newRides.count}
									percentage={{
										color: `${cardDisplays!.newRides.differential < 0 ? 'error' : 'success'}`,
										amount: cardDisplays!.newRides.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>

					<Grid item xs={12} md={4}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="info"
									icon="edit"
									title="Rides Updated"
									tooltip="Calls where the caller intended to modify an existing ride."
									count={cardDisplays!.ridesUpdated.count}
									percentage={{
										color: `${cardDisplays!.ridesUpdated.differential < 0 ? 'error' : 'success'}`,
										amount: cardDisplays!.ridesUpdated.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>

					<Grid item xs={12} md={4}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="warning"
									icon="cancel"
									title="Rides Cancelled"
									tooltip="Calls where the caller intended to cancel a ride."
									count={cardDisplays!.ridesCancelled.count}
									percentage={{
										color: `${cardDisplays!.ridesCancelled.differential > 0 ? 'error' : 'success'}`,
										amount: cardDisplays!.ridesCancelled.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
			<MDBox mt={3}>
				<Grid container spacing={3}>
					<Grid item xs={12} md={4}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="primary"
									icon="event_available"
									title="Booking Confirmations"
									count={cardDisplays!.bookingConfirmations.count}
									percentage={{
										color: `${
											cardDisplays!.bookingConfirmations.differential < 0 ? 'error' : 'success'
										}`,
										amount: cardDisplays!.bookingConfirmations.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>

					<Grid item xs={12} md={4}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								// color="error"
								// icon="person_add"
								// title="Operator Transfer"
								// count={formatCompactNumber(data.cards.operatorTransfer.current)}
								<ComplexStatisticsCard
									color="dark"
									icon={showPercentages ? "auto_mode" : "person_add"}
									title={showPercentages ? "Automation Rate" : "Operator Transfer"}
									tooltip={showPercentages ? "Share of calls automated." : "Share of calls transferred to an operator."}
									count={cardDisplays!.automation.count}
									percentage={{
										color: `${
											cardDisplays!.automation.differential < 0 ? 'error' : 'success'
										}`,
										amount: cardDisplays!.automation.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>

					<Grid item xs={12} md={4}>
						<MDBox mb={1.5}>
							{!isLoading && !error ? (
								<ComplexStatisticsCard
									color="light"
									icon="block"
									title="Excluded Number Transfers"
									count={cardDisplays!.excluded.count}
									percentage={{
										color: `${cardDisplays!.excluded.differential > 0 ? 'error' : 'success'}`,
										amount: cardDisplays!.excluded.amount,
										label: `${activeTimeFrame.replace('Last', 'previous')}`,
									}}
								/>
							) : (
								<LoadingComponent type="box" />
							)}
						</MDBox>
					</Grid>
				</Grid>
			</MDBox>
		</DashboardLayout>
	)
}

export default InboundAnalytics
